import { Injectable, signal, computed } from '@angular/core';

/**
 * All valid permissions in the system, grouped by resource.
 * These must match exactly the strings the backend emits in JWT `permissions[]`.
 */
export const PERMISSIONS = {
  // Invoices
  INVOICES_READ: 'invoices:read',
  INVOICES_CREATE: 'invoices:create',
  INVOICES_UPDATE: 'invoices:update',
  INVOICES_DELETE: 'invoices:delete',
  INVOICES_SEND: 'invoices:send',
  // Payments
  PAYMENTS_READ: 'payments:read',
  PAYMENTS_CREATE: 'payments:create',
  PAYMENTS_DELETE: 'payments:delete',
  // Customers
  CUSTOMERS_READ: 'customers:read',
  CUSTOMERS_CREATE: 'customers:create',
  CUSTOMERS_UPDATE: 'customers:update',
  CUSTOMERS_DELETE: 'customers:delete',
  // Suppliers
  SUPPLIERS_READ: 'suppliers:read',
  SUPPLIERS_CREATE: 'suppliers:create',
  SUPPLIERS_UPDATE: 'suppliers:update',
  SUPPLIERS_DELETE: 'suppliers:delete',
  // Products
  PRODUCTS_READ: 'products:read',
  PRODUCTS_CREATE: 'products:create',
  PRODUCTS_UPDATE: 'products:update',
  PRODUCTS_DELETE: 'products:delete',
  // Categories
  CATEGORIES_READ: 'categories:read',
  CATEGORIES_CREATE: 'categories:create',
  CATEGORIES_UPDATE: 'categories:update',
  CATEGORIES_DELETE: 'categories:delete',
  // Taxes
  TAXES_READ: 'taxes:read',
  TAXES_CREATE: 'taxes:create',
  TAXES_UPDATE: 'taxes:update',
  TAXES_DELETE: 'taxes:delete',
  // Warehouses
  WAREHOUSES_READ: 'warehouses:read',
  WAREHOUSES_CREATE: 'warehouses:create',
  WAREHOUSES_UPDATE: 'warehouses:update',
  WAREHOUSES_DELETE: 'warehouses:delete',
  // Inventory
  INVENTORY_READ: 'inventory:read',
  INVENTORY_ADJUST: 'inventory:adjust',
  INVENTORY_TRANSFER: 'inventory:transfer',
  // Reports
  REPORTS_READ: 'reports:read',
  // Safes
  SAFES_READ: 'safes:read',
  SAFES_CREATE: 'safes:create',
  SAFES_UPDATE: 'safes:update',
  SAFES_TRANSACTION: 'safes:transaction',
  SAFES_TRANSFER: 'safes:transfer',
  // Users
  USERS_READ: 'users:read',
  USERS_CREATE: 'users:create',
  USERS_UPDATE: 'users:update',
  USERS_DELETE: 'users:delete',
  // Roles
  ROLES_READ: 'roles:read',
  ROLES_CREATE: 'roles:create',
  ROLES_UPDATE: 'roles:update',
  ROLES_DELETE: 'roles:delete',
  // Notifications
  NOTIFICATIONS_READ: 'notifications:read',
  NOTIFICATIONS_UPDATE: 'notifications:update',
  // Settings
  SETTINGS_READ: 'settings:read',
  SETTINGS_UPDATE: 'settings:update',
  // Recurring
  RECURRING_READ: 'recurring:read',
  RECURRING_CREATE: 'recurring:create',
  RECURRING_UPDATE: 'recurring:update',
  RECURRING_DELETE: 'recurring:delete',
  // Currencies
  CURRENCIES_READ: 'currencies:read',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/** Sidebar navigation items with required permission to display */
export interface NavItem {
  label: string;
  icon: string;
  route: string;
  permission?: Permission;   // undefined = always visible
  dividerBefore?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { label: 'DASHBOARD', icon: 'pi-chart-bar', route: '/dashboard' },
  { label: 'INVOICES', icon: 'pi-file', route: '/invoices', permission: 'invoices:read', dividerBefore: true },
  { label: 'RECURRING', icon: 'pi-sync', route: '/recurring', permission: 'recurring:read' },
  { label: 'PAYMENTS', icon: 'pi-wallet', route: '/payments', permission: 'payments:read' },
  { label: 'CUSTOMERS', icon: 'pi-users', route: '/customers', permission: 'customers:read', dividerBefore: true },
  { label: 'SUPPLIERS', icon: 'pi-building', route: '/suppliers', permission: 'suppliers:read' },
  { label: 'PRODUCTS', icon: 'pi-box', route: '/products', permission: 'products:read', dividerBefore: true },
  { label: 'CATEGORIES', icon: 'pi-tag', route: '/categories', permission: 'categories:read' },
  { label: 'WAREHOUSES', icon: 'pi-map', route: '/warehouses', permission: 'warehouses:read' },
  { label: 'INVENTORY', icon: 'pi-list-check', route: '/inventory', permission: 'inventory:read' },
  { label: 'TAXES', icon: 'pi-percentage', route: '/taxes', permission: 'taxes:read' },
  { label: 'SAFES', icon: 'pi-shield', route: '/safes', permission: 'safes:read' },
  { label: 'REPORTS', icon: 'pi-chart-line', route: '/reports', permission: 'reports:read', dividerBefore: true },
  { label: 'NOTIFICATIONS', icon: 'pi-bell', route: '/notifications', permission: 'notifications:read' },
  { label: 'TEAM', icon: 'pi-users', route: '/users', permission: 'users:read', dividerBefore: true },
  { label: 'ROLES', icon: 'pi-lock', route: '/roles', permission: 'roles:read' },
  { label: 'SUBSCRIPTIONS', icon: 'pi-star', route: '/subscriptions', permission: 'settings:read' },
  { label: 'SETTINGS', icon: 'pi-cog', route: '/settings', permission: 'settings:read' }];

/**
 * PermissionsService — single source of truth for the current user's permissions.
 *
 * Permissions are decoded from the JWT stored in localStorage.
 * The JWT payload contains: { sub, email, tenantId, role, permissions: string[] }
 *
 * Usage in templates:
 *   *hasPermission="'invoices:create'"
 *
 * Usage in components:
 *   if (!this.permissions.can('invoices:delete')) return;
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {


  /** Raw set of permissions from the decoded JWT */
  private readonly _permissions = signal<Set<string>>(new Set());
  /** The user's base role string (owner | admin | accountant | staff) */
  private readonly _role = signal<string | null>(null);

  readonly role = this._role.asReadonly();

  /** True if the user holds the 'owner' role (full access) */
  readonly isOwner = computed(() => this._role() === 'owner');
  /** True if the user holds the 'owner' or 'admin' role */
  readonly isAdmin = computed(() => ['owner', 'admin'].includes(this._role() ?? ''));

  constructor() {
  }



  /**
   * Set permissions directly from an array (e.g., from login response user object).
   */
  setPermissions(role: string, perms: string[]): void {
    this._role.set(role);
    this._permissions.set(new Set(perms));
  }

  /**
   * Returns true if the current user has the given permission.
   * Owners bypass all checks.
   */
  can(permission: string): boolean {
    if (this.isOwner()) return true;
    return this._permissions().has(permission);
  }

  /**
   * Returns true if the user has ALL of the provided permissions.
   */
  canAll(...permissions: string[]): boolean {
    if (this.isOwner()) return true;
    return permissions.every(p => this._permissions().has(p));
  }

  /**
   * Returns true if the user has ANY of the provided permissions.
   */
  canAny(...permissions: string[]): boolean {
    if (this.isOwner()) return true;
    return permissions.some(p => this._permissions().has(p));
  }

  /** Returns visible nav items for the current user */
  getVisibleNavItems(): NavItem[] {
    return NAV_ITEMS.filter(item =>
      !item.permission || this.can(item.permission)
    );
  }

  /** Clear permissions on logout */
  clear(): void {
    this._permissions.set(new Set());
    this._role.set(null);
  }
}
